import { useEffect, useState } from "react";
import api from "../api";
import { useI18n } from "../i18n";
import { Card } from "@/components/ui/card";
import { eur } from "../helpers";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import { TrendingUp, Percent, DollarSign, BedDouble, Sparkles, Wrench, PiggyBank } from "lucide-react";

function Stat({ icon: Icon, label, value, tone }) {
  return (
    <Card className="flex items-center gap-4 border-slate-200 p-5">
      <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${tone}`}><Icon size={20} /></div>
      <div><div className="text-xl font-semibold text-slate-900 font-head">{value}</div><div className="text-xs text-slate-500">{label}</div></div>
    </Card>
  );
}

const COLORS = ["#1B3A5B", "#B8823C", "#2F7A6E", "#3B6BA8", "#A34A6B"];

export default function Reports() {
  const { t } = useI18n();
  const [r, setR] = useState(null);

  useEffect(() => { api.get("/reports").then((res) => setR(res.data)); }, []);
  if (!r) return <div className="text-slate-400">…</div>;

  const stats = [
    { icon: DollarSign, label: t("totalRevenue"), value: eur(r.total_revenue), tone: "bg-primary text-primary-foreground" },
    { icon: TrendingUp, label: t("adr"), value: eur(r.adr), tone: "bg-blue-100 text-blue-700" },
    { icon: BedDouble, label: t("revpar"), value: eur(r.revpar), tone: "bg-indigo-100 text-indigo-700" },
    { icon: Percent, label: t("occupancy"), value: r.occupancy + "%", tone: "bg-emerald-100 text-emerald-700" },
    { icon: Sparkles, label: t("cleaningCosts"), value: eur(r.cleaning_costs), tone: "bg-cyan-100 text-cyan-700" },
    { icon: Wrench, label: t("maintenanceCosts"), value: eur(r.maintenance_costs), tone: "bg-amber-100 text-amber-800" },
    { icon: PiggyBank, label: t("operatingMargin"), value: eur(r.operating_margin), tone: r.operating_margin >= 0 ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700" },
  ];

  return (
    <div className="space-y-6 fade-in" data-testid="reports-page">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {stats.map((s) => <Stat key={s.label} {...s} />)}
      </div>

      <Card className="border-slate-200 p-6">
        <h3 className="mb-6 font-head text-lg font-semibold text-slate-900">{t("revenuePerApt")}</h3>
        <div className="h-80 w-full" data-testid="revenue-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={r.revenue_per_apartment} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => eur(v)} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 }} />
              <Bar dataKey="revenue" radius={[6, 6, 0, 0]}>
                {r.revenue_per_apartment.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
}
